import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from 'lucide-react';
import { PipelineStage } from '../../types';
interface StageBadgeProps {
  stage?: PipelineStage;
  /** Estados disponibles: si se pasan, la etiqueta abre un desplegable. */
  stages?: PipelineStage[];
  onChange?: (stageId: string) => void;
  className?: string;
}

/**
 * Etiqueta de estado con el color configurado por la empresa.
 * Si recibe `stages` y `onChange`, permite cambiar el estado con un clic.
 *
 * El desplegable se pinta con un portal en `document.body` y posición fija: en
 * la tabla y en las tarjetas del embudo quedaba recortado por el contenedor.
 * Ahora flota por encima de toda la interfaz y se recoloca si no cabe debajo.
 */
export function StageBadge({
  stage,
  stages,
  onChange,
  className = ''
}: StageBadgeProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const anchor = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const width = 224;
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (list.current?.contains(target) || anchor.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    function close() {
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);
  const color = stage?.color ?? '#64748B';
  const name = stage?.name ?? 'Sin estado';
  const pill = <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${className}`} style={{
    backgroundColor: `${color}1A`,
    color
  }} data-id="element-239" data-mp="g8yqfmp2zc5o">
      <span className="h-1.5 w-1.5 flex-none rounded-full" style={{
      backgroundColor: color
    }} aria-hidden="true" data-id="element-240" data-mp="6cnewyxbeqca" />
      {name}
      {stages && <ChevronDownIcon className="h-3 w-3" aria-hidden="true" data-id="element-241" data-mp="141oyhktv5bao" />}
    </span>;
  if (!stages || !onChange) return pill;
  function place() {
    const box = anchor.current?.getBoundingClientRect();
    if (!box) return;
    const height = Math.min(stages!.filter(s => s.active).length * 40 + 8, 288);
    const below = window.innerHeight - box.bottom > height + 12;
    setPos({
      top: below ? box.bottom + 6 : Math.max(8, box.top - height - 6),
      left: Math.min(Math.max(8, box.left), window.innerWidth - width - 8)
    });
  }
  return <>
      <button ref={anchor} type="button" onClick={e => {
      e.stopPropagation();
      if (open) {
        setOpen(false);
        return;
      }
      place();
      setOpen(true);
    }} aria-haspopup="listbox" aria-expanded={open} aria-label={`Cambiar estado, actualmente ${name}`} className="min-h-[32px] rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-id="element-242" data-mp="1pbbkvuu4bevx">
        {pill}
      </button>
      {open && pos && createPortal(<ul ref={list} role="listbox" style={{
      top: pos.top,
      left: pos.left,
      width
    }} className="fixed z-[80] max-h-72 overflow-y-auto rounded-xl border border-border bg-background p-1 shadow-xl" data-id="element-243" data-mp="287jpqxj7xnqp">
            {stages.filter(s => s.active).map(option => <li key={option.id} data-id="element-244" data-mp="2mxqzmov5tyfs">
                <button type="button" role="option" aria-selected={option.id === stage?.id} onClick={e => {
          e.stopPropagation();
          onChange!(option.id);
          setOpen(false);
        }} className={`flex min-h-[40px] w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-surface ${option.id === stage?.id ? 'font-semibold text-foreground' : ''}`} data-id="element-245" data-mp="2n04w925hnipg">
                  <span className="h-2 w-2 flex-none rounded-full" style={{
            backgroundColor: option.color
          }} aria-hidden="true" data-id="element-246" data-mp="6arco4tdju1k" />
                  {option.name}
                </button>
              </li>)}
          </ul>, document.body)}
    </>;
}